import * as React from "react";

import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragCancelEvent,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { Badge } from "@repo/ui/components/badge";
import { Button } from "@repo/ui/components/button";
import {
  ScheduleTimeline,
  ScheduleTimelineItemSurface,
  type ScheduleTimelineDragData,
  type ScheduleTimelineGroup,
  type ScheduleTimelineItem,
  type ScheduleTimelinePreview,
  type ScheduleTimelineResizeData,
  type ScheduleTimelineRowDropData,
} from "@repo/ui/components/schedule-timeline";
import { Tabs, TabsList, TabsTrigger } from "@repo/ui/components/tabs";
import { cn } from "@repo/ui/lib/utils";
import { createFileRoute } from "@tanstack/react-router";
import {
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Cloud,
  FileText,
  Filter,
  MoreVertical,
  Search,
  Send,
  Users,
} from "lucide-react";

export const Route = createFileRoute("/demo")({
  head: () => ({ meta: [{ title: "Schedule Demo" }] }),
  component: RouteComponent,
});

type DemoPerson = {
  id: string;
  name: string;
  role: "Photographer" | "Editor";
  team: "EC" | "MD";
  location: "On-site France" | "Remote";
  avatar: string;
};

type DemoItemData = { personId: string };
type DemoGroup = ScheduleTimelineGroup<DemoItemData>;
type DemoItem = ScheduleTimelineItem<DemoItemData>;

type DemoPersonDragData = { type: "demo-person"; person: DemoPerson };
type DemoPeoplePanelDropData = { type: "demo-people-panel" };
type DemoScheduleView = "event-coverage" | "media-day";
type DemoDragData =
  | DemoPersonDragData
  | DemoPeoplePanelDropData
  | ScheduleTimelineDragData<DemoItemData>
  | ScheduleTimelineResizeData<DemoItemData>
  | ScheduleTimelineRowDropData;

const range = { kind: "time", start: 0, end: 24 * 60, step: 60 } as const;
const snapMinutes = 15;
const minDuration = 15;
const defaultDuration = 60;

const people: DemoPerson[] = [
  {
    id: "michal",
    name: "Michał Bastrzyk",
    role: "Photographer",
    team: "EC",
    location: "On-site France",
    avatar: "MB",
  },
  {
    id: "sheri",
    name: "Sheri Bauch",
    role: "Photographer",
    team: "EC",
    location: "On-site France",
    avatar: "SB",
  },
  {
    id: "stephen",
    name: "Stephen Cook",
    role: "Editor",
    team: "EC",
    location: "Remote",
    avatar: "SC",
  },
  {
    id: "myrtle",
    name: "Myrtle Schumm PhD",
    role: "Photographer",
    team: "EC",
    location: "Remote",
    avatar: "MS",
  },
  {
    id: "david",
    name: "David Neowman",
    role: "Photographer",
    team: "EC",
    location: "Remote",
    avatar: "DN",
  },
  { id: "uunona", name: "uunona", role: "Editor", team: "EC", location: "Remote", avatar: "UN" },
  { id: "jamie", name: "Jamie Wolf", role: "Editor", team: "EC", location: "Remote", avatar: "JW" },
  {
    id: "alex",
    name: "Alex Martin",
    role: "Photographer",
    team: "MD",
    location: "On-site France",
    avatar: "AM",
  },
  {
    id: "patricia",
    name: "Patricia Mowry",
    role: "Editor",
    team: "MD",
    location: "Remote",
    avatar: "PM",
  },
  {
    id: "jordan",
    name: "Jordan Kim",
    role: "Editor",
    team: "MD",
    location: "Remote",
    avatar: "JK",
  },
];

const personById = new Map(people.map((person) => [person.id, person]));

function assignmentItem(id: string, personId: string, start: number, end: number): DemoItem {
  const person = personById.get(personId);
  return {
    id,
    title: person?.name ?? "Unknown person",
    subtitle: `${formatMinute(start)} – ${formatMinute(end)}`,
    start,
    end,
    variant: "assigned",
    data: { personId },
  };
}

const initialGroups: DemoGroup[] = [
  {
    id: "arena",
    title: "Arena",
    eyebrow: "Scene 1",
    subtitle: "Main Stage",
    index: 1,
    rows: [
      {
        id: "arena-photographer",
        label: "Photographer",
        items: [
          assignmentItem("a-1", "david", 10 * 60, 13 * 60),
          assignmentItem("a-2", "michal", 14 * 60, 16 * 60),
        ],
      },
      {
        id: "arena-editor",
        label: "Editor",
        items: [assignmentItem("a-3", "stephen", 14 * 60, 16 * 60)],
      },
    ],
  },
  {
    id: "grid-4",
    title: "Grid",
    eyebrow: "Scene 4",
    subtitle: "Panel Discussion",
    index: 2,
    rows: [
      {
        id: "grid-4-photographer",
        label: "Photographer",
        items: [
          assignmentItem("g4-1", "sheri", 10 * 60, 12 * 60 + 30),
          assignmentItem("g4-2", "jamie", 13 * 60 + 30, 16 * 60 + 30),
        ],
      },
      {
        id: "grid-4-editor",
        label: "Editor",
        items: [assignmentItem("g4-3", "uunona", 10 * 60, 13 * 60)],
      },
    ],
  },
  {
    id: "atrium-7",
    title: "Atrium",
    eyebrow: "Scene 7",
    subtitle: "Sponsor Booths",
    index: 3,
    rows: [
      {
        id: "atrium-7-photographer",
        label: "Photographer",
        items: [assignmentItem("at7-1", "myrtle", 14 * 60, 17 * 60)],
      },
      {
        id: "atrium-7-editor",
        label: "Editor",
        items: [assignmentItem("at7-2", "uunona", 14 * 60, 17 * 60)],
      },
    ],
  },
  {
    id: "atrium-2",
    title: "Atrium",
    eyebrow: "Scene 2",
    subtitle: "Main Stage",
    index: 4,
    rows: [
      {
        id: "atrium-2-photographer",
        label: "Photographer",
        items: [assignmentItem("at2-1", "alex", 9 * 60, 13 * 60)],
      },
      {
        id: "atrium-2-editor",
        label: "Editor",
        items: [assignmentItem("at2-2", "jamie", 13 * 60, 16 * 60)],
      },
    ],
  },
  {
    id: "grid-3",
    title: "Grid",
    eyebrow: "Scene 3",
    subtitle: "Workshop Area",
    index: 5,
    rows: [
      {
        id: "grid-3-photographer",
        label: "Photographer",
        items: [assignmentItem("g3-1", "sheri", 14 * 60, 17 * 60)],
      },
      {
        id: "grid-3-editor",
        label: "Editor",
        items: [assignmentItem("g3-2", "patricia", 10 * 60, 13 * 60)],
      },
    ],
  },
];

function formatMinute(minute: number) {
  const hours = Math.floor(minute / 60);
  const mins = minute % 60;
  return `${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}`;
}

function snap(value: number) {
  return Math.round(value / snapMinutes) * snapMinutes;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function sortItems(items: DemoItem[]) {
  return items.toSorted((a, b) => Number(a.start) - Number(b.start));
}

function updateItem(groups: DemoGroup[], nextItem: DemoItem, groupId: string, rowId: string) {
  return groups.map((group) => ({
    ...group,
    rows: group.rows.map((row) => {
      const items = row.items.filter((existingItem) => existingItem.id !== nextItem.id);
      if (group.id === groupId && row.id === rowId) items.push(nextItem);
      return { ...row, items: sortItems(items) };
    }),
  }));
}

function removeItem(groups: DemoGroup[], itemId: string) {
  return groups.map((group) => ({
    ...group,
    rows: group.rows.map((row) => ({
      ...row,
      items: row.items.filter((existingItem) => existingItem.id !== itemId),
    })),
  }));
}

function getAssignmentCounts(groups: DemoGroup[]) {
  const counts = new Map<string, number>();
  for (const group of groups) {
    for (const row of group.rows) {
      for (const assignment of row.items) {
        const personId = assignment.data?.personId;
        if (personId) counts.set(personId, (counts.get(personId) ?? 0) + 1);
      }
    }
  }
  return counts;
}

function getMovedPreview(
  active: ScheduleTimelineDragData<DemoItemData>,
  over: ScheduleTimelineRowDropData,
  deltaX: number,
): ScheduleTimelinePreview | undefined {
  if (
    active.rangeKind !== "time" ||
    typeof active.item.start !== "number" ||
    typeof active.item.end !== "number"
  )
    return;

  const duration = active.item.end - active.item.start;
  const start = clamp(
    snap(active.item.start + deltaX * active.valuePerPixel),
    range.start,
    range.end - duration,
  );
  const end = start + duration;

  return {
    id: active.item.id,
    groupId: over.groupId,
    rowId: over.rowId,
    title: active.item.title,
    subtitle: `${formatMinute(start)} – ${formatMinute(end)}`,
    meta: active.item.meta,
    mode: "ghost",
    start,
    end,
    variant: active.item.variant,
  };
}

function getResizePreview(active: ScheduleTimelineResizeData<DemoItemData>, deltaX: number) {
  if (
    active.rangeKind !== "time" ||
    typeof active.item.start !== "number" ||
    typeof active.item.end !== "number"
  )
    return;

  const delta = deltaX * active.valuePerPixel;
  const start =
    active.edge === "start"
      ? clamp(snap(active.item.start + delta), range.start, active.item.end - minDuration)
      : active.item.start;
  const end =
    active.edge === "end"
      ? clamp(snap(active.item.end + delta), active.item.start + minDuration, range.end)
      : active.item.end;

  return {
    id: active.item.id,
    groupId: active.groupId,
    rowId: active.rowId,
    title: active.item.title,
    subtitle: `${formatMinute(start)} – ${formatMinute(end)}`,
    meta: active.item.meta,
    mode: "solid",
    start,
    end,
    variant: active.item.variant,
  } satisfies ScheduleTimelinePreview;
}

function getPersonDropPreview(
  person: DemoPerson,
  over: ScheduleTimelineRowDropData,
  activeRect?: { left: number } | null,
) {
  const rowEl = document.querySelector<HTMLElement>(
    `[data-schedule-timeline-row-id="${over.rowId}"]`,
  );
  if (!rowEl || !activeRect) return;

  const rowRect = rowEl.getBoundingClientRect();
  const rawStart =
    range.start + ((activeRect.left - rowRect.left) / rowRect.width) * (range.end - range.start);
  const start = clamp(snap(rawStart), range.start, range.end - defaultDuration);
  const end = start + defaultDuration;

  return {
    groupId: over.groupId,
    rowId: over.rowId,
    title: person.name,
    subtitle: `${formatMinute(start)} – ${formatMinute(end)}`,
    mode: "ghost",
    start,
    end,
    variant: "assigned",
  } satisfies ScheduleTimelinePreview;
}

function getPreview(event: DragMoveEvent | DragEndEvent) {
  const active = event.active.data.current as DemoDragData | undefined;
  const over = event.over?.data.current as DemoDragData | undefined;

  if (!active) return;
  if (active.type === "schedule-timeline-resize") return getResizePreview(active, event.delta.x);
  if (active.type === "schedule-timeline-item" && over?.type === "schedule-timeline-row") {
    return getMovedPreview(active, over, event.delta.x);
  }
  if (active.type === "demo-person" && over?.type === "schedule-timeline-row") {
    return getPersonDropPreview(active.person, over, event.active.rect.current.translated);
  }
}

function RouteComponent() {
  const [timelineGroups, setGroups] = React.useState(() => initialGroups);
  const [preview, setPreview] = React.useState<ScheduleTimelinePreview>();
  const [activeDrag, setActiveDrag] = React.useState<DemoDragData>();
  const [overlayWidth, setOverlayWidth] = React.useState<number>();
  const [scheduleView, setScheduleView] = React.useState<DemoScheduleView>("event-coverage");
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const assignmentCounts = React.useMemo(
    () => getAssignmentCounts(timelineGroups),
    [timelineGroups],
  );
  const assignmentTotal = [...assignmentCounts.values()].reduce((sum, count) => sum + count, 0);
  const assignedPeople = [...assignmentCounts.values()].filter(Boolean).length;

  const handleDragStart = React.useCallback((event: DragStartEvent) => {
    const data = event.active.data.current as DemoDragData | undefined;
    const activeRect = event.active.rect.current.initial;
    setActiveDrag(data?.type === "schedule-timeline-resize" ? undefined : data);
    setOverlayWidth(data?.type === "schedule-timeline-resize" ? undefined : activeRect?.width);
  }, []);

  const handleDragMove = React.useCallback((event: DragMoveEvent) => {
    setPreview(getPreview(event));
  }, []);

  const clearDrag = React.useCallback((_event?: DragCancelEvent) => {
    setActiveDrag(undefined);
    setOverlayWidth(undefined);
    setPreview(undefined);
  }, []);

  const handleDragEnd = React.useCallback((event: DragEndEvent) => {
    const nextPreview = getPreview(event);
    const active = event.active.data.current as DemoDragData | undefined;
    const over = event.over?.data.current as DemoDragData | undefined;

    setActiveDrag(undefined);
    setOverlayWidth(undefined);
    setPreview(undefined);

    if (!active) return;

    if (active.type === "schedule-timeline-item" && over?.type === "demo-people-panel") {
      setGroups((currentGroups) => removeItem(currentGroups, active.item.id));
      return;
    }

    if (
      active.type === "schedule-timeline-item" &&
      over?.type === "schedule-timeline-row" &&
      nextPreview
    ) {
      setGroups((currentGroups) =>
        updateItem(
          currentGroups,
          {
            ...active.item,
            start: nextPreview.start,
            end: nextPreview.end,
            subtitle: `${formatMinute(Number(nextPreview.start))} – ${formatMinute(Number(nextPreview.end))}`,
          },
          nextPreview.groupId,
          nextPreview.rowId,
        ),
      );
      return;
    }

    if (active.type === "schedule-timeline-resize" && nextPreview) {
      setGroups((currentGroups) =>
        updateItem(
          currentGroups,
          {
            ...active.item,
            start: nextPreview.start,
            end: nextPreview.end,
            subtitle: `${formatMinute(Number(nextPreview.start))} – ${formatMinute(Number(nextPreview.end))}`,
          },
          active.groupId,
          active.rowId,
        ),
      );
      return;
    }

    if (active.type === "demo-person" && over?.type === "schedule-timeline-row" && nextPreview) {
      setGroups((currentGroups) =>
        updateItem(
          currentGroups,
          {
            id: `assignment-${active.person.id}-${Date.now()}`,
            title: active.person.name,
            subtitle: `${formatMinute(Number(nextPreview.start))} – ${formatMinute(Number(nextPreview.end))}`,
            start: nextPreview.start,
            end: nextPreview.end,
            variant: "assigned",
            data: { personId: active.person.id },
          },
          nextPreview.groupId,
          nextPreview.rowId,
        ),
      );
    }
  }, []);

  return (
    <DndContext
      autoScroll={false}
      sensors={sensors}
      onDragCancel={clearDrag}
      onDragEnd={handleDragEnd}
      onDragMove={handleDragMove}
      onDragStart={handleDragStart}
    >
      <main className="flex h-svh min-h-0 flex-col bg-background text-foreground">
        <DemoTopbar assignmentTotal={assignmentTotal} />
        <DemoStatusStrip assignmentTotal={assignmentTotal} assignedPeople={assignedPeople} />
        <section className="grid min-h-0 flex-1 grid-cols-[324px_minmax(0,1fr)] gap-3 p-3 pt-0">
          <PeoplePanel assignmentCounts={assignmentCounts} />
          <div className="flex min-w-0 flex-col gap-3 overflow-hidden">
            <TimelineToolbar scheduleView={scheduleView} onScheduleViewChange={setScheduleView} />
            <ScheduleTimeline
              className="[--schedule-timeline-scrollbar-size:12px] [--schedule-timeline-scrollbar-thumb-hover:color-mix(in_oklab,var(--foreground)_42%,transparent)] [--schedule-timeline-scrollbar-thumb:color-mix(in_oklab,var(--foreground)_28%,transparent)] [--schedule-timeline-scrollbar-track:color-mix(in_oklab,var(--border)_55%,transparent)]"
              groups={timelineGroups}
              interaction={{ kind: "editable", resize: true }}
              labels={{ emptyRow: "Drop a person here" }}
              preview={preview}
              range={range}
            />
          </div>
        </section>
      </main>
      <DragOverlay dropAnimation={null}>
        {activeDrag ? <DemoDragOverlay drag={activeDrag} overlayWidth={overlayWidth} /> : null}
      </DragOverlay>
    </DndContext>
  );
}

function DateNavigator() {
  return (
    <div className="flex items-center gap-2">
      <Button className="size-9" size="icon" variant="outline">
        <ChevronLeft className="size-4" />
      </Button>
      <Button className="h-9 min-w-40 justify-between" variant="outline">
        <span className="inline-flex items-center gap-2">
          <CalendarDays className="size-4" /> Sun, Jun 7
        </span>
        <ChevronDown className="size-4 opacity-60" />
      </Button>
      <Button className="size-9" size="icon" variant="outline">
        <ChevronRight className="size-4" />
      </Button>
      <Button className="h-9" variant="outline">
        Today
      </Button>
    </div>
  );
}

function ScheduleTabs({
  value,
  onValueChange,
}: {
  value: DemoScheduleView;
  onValueChange: (value: DemoScheduleView) => void;
}) {
  return (
    <Tabs value={value} onValueChange={(nextValue) => onValueChange(nextValue as DemoScheduleView)}>
      <TabsList className="h-9">
        <TabsTrigger className="px-4" value="event-coverage">
          Event Coverage
        </TabsTrigger>
        <TabsTrigger className="px-4" value="media-day">
          Media Day
        </TabsTrigger>
      </TabsList>
    </Tabs>
  );
}

function DemoTopbar({ assignmentTotal }: { assignmentTotal: number }) {
  return (
    <header className="flex h-14 shrink-0 items-center border-b px-4">
      <div className="flex min-w-0 items-center gap-5">
        <div className="text-lg font-bold tracking-tight">WORKGRID</div>
        <DateNavigator />
      </div>
      <div className="ml-auto flex items-center gap-5 text-sm">
        <span className="inline-flex items-center gap-2 text-muted-foreground">
          <Cloud className="size-4 text-primary" /> Discord:{" "}
          <span className="text-foreground">Connected</span>
        </span>
        <span className="inline-flex items-center gap-2 text-muted-foreground">
          <Users className="size-4" /> {assignmentTotal} assignments
        </span>
        <span className="inline-flex items-center gap-2 text-muted-foreground">
          <CheckCircle2 className="size-4 text-primary" /> All changes saved
        </span>
        <Button size="icon" variant="ghost">
          <MoreVertical className="size-4" />
        </Button>
      </div>
    </header>
  );
}

function DemoStatusStrip({
  assignmentTotal,
  assignedPeople,
}: {
  assignmentTotal: number;
  assignedPeople: number;
}) {
  return (
    <section className="m-3 grid shrink-0 grid-cols-[1fr_1fr_1fr_1fr_auto] overflow-hidden rounded-lg border bg-card text-sm shadow-sm">
      <StatusCell
        icon={<CheckCircle2 className="size-5 text-primary" />}
        title="Saved changes"
        description="Last edit just now"
      />
      <StatusCell
        icon={<FileText className="size-5" />}
        title="Draft"
        description="Not published"
      />
      <StatusCell
        icon={<Cloud className="size-5" />}
        title="Discord ready"
        description="Connected workspace"
      />
      <StatusCell
        icon={<CalendarDays className="size-5" />}
        title="Sun, Jun 7"
        description={`${assignedPeople} people · ${assignmentTotal} assignments`}
      />
      <div className="flex items-center p-3">
        <Button className="h-10 gap-2 px-5">
          <Send className="size-4" /> Publish update
        </Button>
      </div>
    </section>
  );
}

function StatusCell({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="flex items-center gap-3 border-r p-4 last:border-r-0">
      <div className="text-muted-foreground">{icon}</div>
      <div className="min-w-0">
        <div className="truncate font-semibold">{title}</div>
        <div className="truncate text-muted-foreground">{description}</div>
      </div>
    </div>
  );
}

function TimelineToolbar({
  scheduleView,
  onScheduleViewChange,
}: {
  scheduleView: DemoScheduleView;
  onScheduleViewChange: (value: DemoScheduleView) => void;
}) {
  return (
    <div className="flex shrink-0 items-center gap-2">
      <ScheduleTabs value={scheduleView} onValueChange={onScheduleViewChange} />
      <div className="ml-auto flex items-center gap-2">
        <div className="flex h-9 w-72 items-center gap-2 rounded-md border bg-background px-3 text-sm text-muted-foreground">
          <Search className="size-4" /> Search roles, scenes, people...
        </div>
        <Button className="gap-2" variant="outline">
          <Filter className="size-4" /> Filters
        </Button>
        <Button className="gap-2" variant="outline">
          Week <ChevronDown className="size-4" />
        </Button>
      </div>
    </div>
  );
}

function PeoplePanel({ assignmentCounts }: { assignmentCounts: Map<string, number> }) {
  const { isOver, setNodeRef } = useDroppable({
    id: "demo-people-panel",
    data: { type: "demo-people-panel" } satisfies DemoPeoplePanelDropData,
  });
  const groups = React.useMemo(() => {
    const grouped = new Map<string, DemoPerson[]>();
    for (const person of people) {
      const key = `${person.team} — ${person.role}s`;
      grouped.set(key, [...(grouped.get(key) ?? []), person]);
    }
    return [...grouped.entries()];
  }, []);

  return (
    <aside className="flex min-h-0 flex-col overflow-hidden rounded-xl border bg-card/60">
      <div className="border-b p-4">
        <h2 className="font-semibold tracking-tight">Coverage Summary</h2>
        <div className="mt-4 grid gap-3 text-sm">
          <SummaryRow
            label="Assignments"
            value={[...assignmentCounts.values()].reduce((sum, count) => sum + count, 0)}
          />
          <SummaryRow
            label="Assigned people"
            value={[...assignmentCounts.values()].filter(Boolean).length}
          />
          <SummaryRow
            label="Available people"
            value={people.length - [...assignmentCounts.values()].filter(Boolean).length}
          />
        </div>
      </div>
      <div
        className={cn(
          "min-h-0 flex-1 overflow-y-auto p-3 transition-colors",
          isOver && "bg-accent/45",
        )}
        ref={setNodeRef}
      >
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-semibold tracking-tight">People</h2>
          <Badge variant="outline">{people.length}</Badge>
        </div>
        <div className="space-y-4">
          {groups.map(([label, members]) => (
            <div key={label}>
              <div className="mb-2 flex items-center justify-between text-sm font-medium">
                <span>{label}</span>
                <Badge variant="outline">{members.length}</Badge>
              </div>
              <div className="space-y-2">
                {members.map((person) => (
                  <PersonCard
                    key={person.id}
                    assignmentCount={assignmentCounts.get(person.id) ?? 0}
                    person={person}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </aside>
  );
}

function SummaryRow({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex items-center justify-between gap-3 text-muted-foreground">
      <span>{label}</span>
      <span className="font-semibold text-foreground">{value}</span>
    </div>
  );
}

function PersonCard({ person, assignmentCount }: { person: DemoPerson; assignmentCount: number }) {
  const { attributes, isDragging, listeners, setNodeRef } = useDraggable({
    id: `demo-person:${person.id}`,
    data: { type: "demo-person", person } satisfies DemoPersonDragData,
  });

  return (
    <div
      className={cn(
        "cursor-grab rounded-lg border bg-background p-2 shadow-sm active:cursor-grabbing",
        isDragging && "opacity-40",
      )}
      ref={setNodeRef}
      {...attributes}
      {...listeners}
    >
      <PersonCardBody assignmentCount={assignmentCount} person={person} />
    </div>
  );
}

function PersonCardBody({
  person,
  assignmentCount,
}: {
  person: DemoPerson;
  assignmentCount: number;
}) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground">
        {person.avatar}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">{person.name}</div>
        <div className="mt-1 flex gap-1 overflow-hidden">
          <Badge className="h-5 shrink-0 px-1.5 text-[10px]" variant="secondary">
            {person.role}
          </Badge>
          <Badge className="h-5 min-w-0 truncate px-1.5 text-[10px]" variant="outline">
            {person.location}
          </Badge>
        </div>
      </div>
      <Badge className="shrink-0" variant={assignmentCount ? "default" : "outline"}>
        {assignmentCount ? "Assigned" : "Available"}
      </Badge>
    </div>
  );
}

function DemoDragOverlay({ drag, overlayWidth }: { drag: DemoDragData; overlayWidth?: number }) {
  if (drag.type === "demo-person") {
    return (
      <div className="w-72 rounded-lg border bg-background p-2 shadow-2xl">
        <PersonCardBody assignmentCount={0} person={drag.person} />
      </div>
    );
  }

  if (drag.type === "schedule-timeline-item") {
    return <TimelineItemOverlay overlayWidth={overlayWidth} timelineItem={drag.item} />;
  }

  return null;
}

function TimelineItemOverlay({
  overlayWidth,
  timelineItem,
}: {
  overlayWidth?: number;
  timelineItem: DemoItem;
}) {
  return (
    <ScheduleTimelineItemSurface
      className="shadow-2xl"
      meta={timelineItem.meta}
      style={{ width: overlayWidth }}
      subtitle={timelineItem.subtitle}
      title={timelineItem.title}
      variant={timelineItem.variant}
    />
  );
}
